import * as React from "react";
import {
    AlertDialog,
    AlertDialogContent,
    Button,
} from "@jhool-io/fe-components";
import { useQueryClient } from "@tanstack/react-query";
import { formatDate } from "../../../../utils/helpers";
import { IPayer } from "../../types/payers.types";
import { useDeletePayer } from "../../hooks/payers.mutations";
import useToast from "../../../../hooks/useToast";
import { getStateInShortForm } from "../../../../utils/helpers/us-states/us-states";

interface Props {
    selectedPayer: IPayer;
    setIsEditView: React.Dispatch<React.SetStateAction<boolean>>;
    onDeleteComplete: () => void;
}

export default function PayerDetailsInfo({
    selectedPayer,
    setIsEditView,
    onDeleteComplete,
}: Props) {
    const deletePayer = useDeletePayer();
    const queryClient = useQueryClient();

    const { toast } = useToast();

    const [showDeleteModal, setShowDeleteModal] = React.useState(false);

    const handleDeletePayer = () => {
        deletePayer.mutate(
            { insurance_provider_id: selectedPayer.insurance_provider_id },
            {
                onSuccess(res) {
                    queryClient.invalidateQueries({ queryKey: ["payers"] });
                    toast({
                        mode: "success",
                        message: res.message || "Payer deleted successfully",
                    });

                    // close modal
                    setShowDeleteModal(false);
                    onDeleteComplete();
                },
                onError(err) {
                    toast({
                        mode: "error",
                        message:
                            err.response?.data.message ||
                            "Could not delete payer. Please try again",
                    });
                },
            }
        );
    };

    return (
        <>
            <AlertDialog open={showDeleteModal}>
                <AlertDialogContent
                    title="Are you sure you want to delete this payer?"
                    description=" Deleting this payer is permanent, and can't be  reversed."
                    onCancelClick={() => setShowDeleteModal(false)}
                    isSaveBtnDisabled={deletePayer.isLoading}
                    saveText="Delete Payer"
                    onSaveClick={handleDeletePayer}
                    isDeleting
                />
            </AlertDialog>

            <div>
                <div className="flex justify-between mb-[--spacing-header] [border-bottom:1px_solid_var(--color-stroke)] pb-[--padding-card-y-lg] flex-wrap gap-[--spacing-items]">
                    <h4 className="text-title font-semibold">Payer Details</h4>

                    <div className="flex gap-[--spacing-items]">
                        <Button
                            variant="secondary"
                            onClick={() => setIsEditView(true)}
                        >
                            Edit Payer
                        </Button>
                        <Button
                            className="bg-danger text-white hover:bg-danger"
                            onClick={() => setShowDeleteModal(true)}
                        >
                            Delete Payer
                        </Button>
                    </div>
                </div>

                <div className="grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-[--spacing-items]">
                    <div>
                        <h4 className="section-info">PROVIDER&apos;S NAME</h4>
                        <p className="section-value">{selectedPayer.name}</p>
                    </div>
                    <div>
                        <h4 className="section-info">PAYER&apos;S ID</h4>
                        <p className="section-value">
                            {selectedPayer.payer_id
                                ? selectedPayer.payer_id
                                : "--"}
                        </p>
                    </div>
                    <div>
                        <h4 className="section-info">PAYER&apos;S NAME</h4>
                        <p className="section-value">
                            {selectedPayer.payer_name
                                ? selectedPayer.payer_name
                                : "--"}
                        </p>
                    </div>
                    <div>
                        <h4 className="section-info">PAYER&apos;S ADDRESS 1</h4>
                        <p className="section-value">
                            {selectedPayer.payer_address_1
                                ? selectedPayer.payer_address_1
                                : "--"}
                        </p>
                    </div>
                    <div>
                        <h4 className="section-info">PAYER&apos;S ADDRESS 2</h4>
                        <p className="section-value">
                            {selectedPayer.payer_address_2
                                ? selectedPayer.payer_address_2
                                : "--"}
                        </p>
                    </div>
                    <div>
                        <h4 className="section-info">PAYER&apos;S CITY</h4>
                        <p className="section-value">
                            {selectedPayer.payer_city
                                ? selectedPayer.payer_city
                                : "--"}
                        </p>
                    </div>
                    <div>
                        <h4 className="section-info">PAYER&apos;S STATE</h4>
                        <p className="section-value">
                            {getStateInShortForm(selectedPayer.payer_state)}
                        </p>
                    </div>
                    <div>
                        <h4 className="section-info">PAYER&apos;S ZIPCODE</h4>
                        <p className="section-value">
                            {selectedPayer.payer_zipcode
                                ? selectedPayer.payer_zipcode
                                : "--"}
                        </p>
                    </div>
                    <div>
                        <h4 className="section-info">PAYER ENROLLED</h4>
                        <p className="section-value">
                            {selectedPayer.payer_enrolled ? "YES" : "NO"}
                        </p>
                    </div>
                    <div>
                        <h4 className="section-info">
                            ATTACHMENT PAYER&apos;S DETAILS
                        </h4>
                        <p className="section-value">
                            {selectedPayer.attachment_payer_id || "--"}
                        </p>
                    </div>
                    <div>
                        <h4 className="section-info">
                            ATTACHMENT PAYER&apos;S NAME
                        </h4>
                        <p className="section-value">
                            {selectedPayer.attachment_payer_name || "--"}
                        </p>
                    </div>
                    <div>
                        <h4 className="section-info">UPDATED AT</h4>
                        <p className="section-value">
                            {formatDate(selectedPayer.updated_at)}
                        </p>
                    </div>
                </div>
            </div>
        </>
    );
}
